import {HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {Observable, throwError} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {AuthService} from 'src/app/auth/auth.service';
import {AlertService} from 'src/app/main/alert.service';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private handledSessionFailureToken: string;

  constructor(
    private router: Router,
    private auth: AuthService,
    private toastr: AlertService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        const skipToast = req.headers.has('X-Skip-Error-Toast');
        const sessionFailureStatus = this.sessionFailureStatus(error);

        // Session authentication failures must never be suppressed by callers
        // that only want to hide an error toast. The gateway now returns a
        // standard coded 401; true 440 and the legacy 500/body-440 wrapper remain
        // supported during rolling deployments. All three paths share the same
        // token comparison and once-only side effects.
        if (sessionFailureStatus !== null) {
          if (this.clearRequestSessionOnce(req)) {
            const raw = this.extractMessage(error);
            const clean = raw && !raw.startsWith('<') ? raw : '';

            if (!skipToast) {
              this.toastr.error(
                clean || 'Your session has expired. Please sign in again.',
                sessionFailureStatus.toString(),
              );
            }
            this.router.navigateByUrl('/login', {replaceUrl: true});
          }
          return throwError(error);
        }

        // Decide 401/403 from the real HTTP status, not the parsed body. The
        // gateway returns them as an HTML error page (sendError) and mw as JSON,
        // so the body-based branches below would miss them and no toast would show.

        // 403: permission denied. Session is valid (the role just lacks access),
        // so do NOT log out here.
        if (error.status === 403) {
          if (!skipToast) {
            this.toastr.error(this.forbiddenMessage(this.extractMessage(error)), '403');
          }
          return throwError(error);
        }

        // 401: unauthorized. Session authentication failures were handled above;
        // an ordinary authorization failure only informs the user.
        if (error.status === 401) {
          const raw = this.extractMessage(error);
          const clean = raw && !raw.startsWith('<') ? raw : '';
          if (!skipToast) {
            this.toastr.error(clean || 'You are not authorized for this action.', '401');
          }
          return throwError(error);
        }

        let msg: any;

        if (error.error instanceof String || typeof error.error === 'string') {
          const raw = error.error.toString();

          try {
            msg = JSON.parse(raw);
          } catch (e) {
            msg = {
              status: error.status,
              message: raw
            };
          }

          if (msg.status === 400) {
            if ((error.url || '').includes('keepalive')) {
              if (this.clearRequestSession(req)) {
                if (!skipToast) {
                  this.toastr.error(msg.message, msg.status.toString());
                }
                this.router.navigateByUrl('/login', {replaceUrl: true});
              }
              return throwError(error);
            }
            if (!skipToast) {
              this.toastr.error(msg.message, msg.status.toString());
            }
          } else if (msg.status === 404) {
            if (!skipToast) {
              this.toastr.error(msg.message, msg.status.toString());
            }
          } else if (msg.status === 500) {
            // const navigationExtras: NavigationExtras = {
            //   state: { error: msg.error },
            // };
            // this.router.navigateByUrl('/server-error', navigationExtras);
            if (!skipToast) {
              this.toastr.error(msg.message, msg.status.toString());
            }
          }
        } else if (error.error instanceof Object) {
          msg = error.error;
          if (msg.status === 400) {
            if ((error.url || '').includes('keepalive')) {
              if (this.clearRequestSession(req)) {
                if (!skipToast) {
                  this.toastr.error(msg.message, msg.status.toString());
                }
                this.router.navigateByUrl('/login', {replaceUrl: true});
              }
              return throwError(error);
            }
            if (!skipToast) {
              this.toastr.error(msg.message, msg.status.toString());
            }
          } else if (msg.status === 404) {
            if (!skipToast) {
              this.toastr.error(msg.message, msg.status.toString());
            }
          } else if (msg.status === 500) {
            // const navigationExtras: NavigationExtras = {
            //   state: { error: msg.error },
            // };
            // this.router.navigateByUrl('/server-error', navigationExtras);
            if (!skipToast) {
              this.toastr.error(msg.message, msg.status.toString());
            }
          }
        }

        return throwError(error);
      }),
    );
  }

  /**
   * Returns the status to display for a recognized session failure. The stable
   * SESSION_EXPIRED code is authoritative. A narrow message fallback supports
   * older gateway/middleware versions without turning permission-related 401s
   * that merely mention a session or token into logouts.
   */
  private sessionFailureStatus(error: HttpErrorResponse): number | null {
    const body = this.extractBody(error);

    if (error.status === 440) {
      return 440;
    }
    if (error.status === 500 && body && Number(body.status) === 440) {
      return 440;
    }
    if (error.status !== 401) {
      return null;
    }
    if (body && String(body.code || '').toUpperCase() === 'SESSION_EXPIRED') {
      return 401;
    }

    return this.isLegacySessionFailureMessage(this.extractMessage(error)) ? 401 : null;
  }

  private isLegacySessionFailureMessage(message: string): boolean {
    const normalized = (message || '').trim().toLowerCase().replace(/\s+/g, ' ');
    if (!normalized) {
      return false;
    }

    return /^(?:your |the )?session (?:has |is )?(?:expired|invalid|missing|not found|no longer valid|expired or invalid)[.!]?(?: please (?:sign|log) in again[.!]?)?$/.test(normalized)
      || /^(?:expired|invalid|missing) session[.!]?$/.test(normalized)
      || /^(?:your |the )?(?:access |authentication )?token (?:has |is )?(?:expired|invalid|missing|not found|no longer valid)[.!]?(?: please (?:sign|log) in again[.!]?)?$/.test(normalized)
      || /^(?:expired|invalid|missing) (?:access |authentication )?token[.!]?$/.test(normalized)
      || /^(?:authentication required|unauthenticated|please (?:sign|log) in again)[.!]?$/.test(normalized)
      || /^oturum (?:süresi doldu|geçersiz|bulunamadı)[.!]?$/.test(normalized);
  }

  /**
   * The gateway returns 403 as an HTML error page (sendError) while mw returns a
   * clean JSON message. Show the backend message when it is a plain string,
   * otherwise a friendly default.
   */
  private forbiddenMessage(message: any): string {
    const text = message == null ? '' : String(message).trim();
    if (text && !text.startsWith('<')) {
      return text;
    }
    return 'You are not authorized to perform this action.';
  }

  private extractBody(error: HttpErrorResponse): any {
    let body: any = error ? error.error : null;
    if (typeof body === 'string') {
      try {
        body = JSON.parse(body);
      } catch (e) {
        return body;
      }
    }
    return body;
  }

  /**
   * Pulls a message out of the error body regardless of shape: a JSON object
   * (mw), a JSON string, or a plain/HTML string (gateway). Returns '' when there
   * is nothing usable so forbiddenMessage falls back to its default.
   */
  private extractMessage(error: HttpErrorResponse): string {
    const body = this.extractBody(error);
    if (body && typeof body === 'object' && body.message) {
      return body.message;
    }
    return typeof body === 'string' ? body : '';
  }

  private clearRequestSessionOnce(req: HttpRequest<any>): boolean {
    const requestToken = req.headers.get('Authorization');
    const failureToken = requestToken || 'missing-token';
    if (this.handledSessionFailureToken === failureToken) {
      return false;
    }
    if (!this.clearRequestSession(req)) {
      return false;
    }
    this.handledSessionFailureToken = failureToken;
    return true;
  }

  private clearRequestSession(req: HttpRequest<any>): boolean {
    const requestToken = req.headers.get('Authorization');
    if (requestToken !== this.auth.getToken()) {
      return false;
    }
    return requestToken
      ? this.auth.clearSession(requestToken)
      : this.auth.clearSession();
  }
}
